/* eslint-disable no-param-reassign -- gate entries and three.js objects are mutable records updated in place */
import { estimateTargetBytes } from './options'
import type { Entry, ReflectorGateOptions } from './types'

export interface TargetPool {
  getResidentBytes: () => number
  applyMaterial: (entry: Entry) => void
  release: (entry: Entry) => void
  /**
   * Hands targets to visible mirrors that don't have one yet. A visible
   * owner is never evicted, since swapping a live mirror to the fallback
   * material reads as the mirror turning off. When the budget is full the
   * newcomer first evicts hidden owners (longest hidden first), then shrinks
   * its own target.
   */
  assign: (waiting: Entry[], owners: Entry[]) => void
}

const byDistance = (a: Entry, b: Entry) =>
  a.distanceToCamera - b.distanceToCamera

const byLongestHidden = (a: Entry, b: Entry) => b.framesHidden - a.framesHidden

/**
 * Owns the GPU memory budget for mirror render targets: which mirrors hold
 * a target, at what size, and how much is resident in total.
 */
export function createTargetPool(options: ReflectorGateOptions): TargetPool {
  let residentBytes = 0

  const fits = (bytes: number) => {
    const budget = Math.max(0, options.targetMemoryBudgetBytes)
    return residentBytes === 0 || residentBytes + bytes <= budget
  }

  /** Largest edge, halving from full size, that fits the remaining budget. */
  const sizeThatFits = (entry: Entry): number | undefined => {
    let size = entry.textureSize
    const min = Math.min(options.minTextureSize, size)
    for (;;) {
      if (fits(estimateTargetBytes(size, size, entry.samples))) {
        return size
      }
      if (size <= min) return undefined
      size = Math.max(min, Math.floor(size / 2))
    }
  }

  const applyMaterial = (entry: Entry) => {
    const next = entry.owner ? entry.reflectorMaterial : entry.fallbackMaterial
    if (entry.reflector.material !== next) {
      entry.reflector.material = next
    }
  }

  const acquire = (entry: Entry, size: number) => {
    if (size !== entry.currentSize) {
      entry.reflector.getRenderTarget().setSize(size, size)
      entry.currentSize = size
    }
    entry.targetBytes = estimateTargetBytes(size, size, entry.samples)
    entry.owner = true
    residentBytes += entry.targetBytes
    applyMaterial(entry)
  }

  const release = (entry: Entry) => {
    if (!entry.owner) return
    entry.reflector.getRenderTarget().dispose()
    entry.owner = false
    residentBytes -= entry.targetBytes
    entry.hasSnapshot = false
    entry.renderedOnce = false
    entry.intervalCounter = 0
    applyMaterial(entry)
  }

  const assign = (waiting: Entry[], owners: Entry[]) => {
    if (waiting.length === 0) return

    waiting.sort(byDistance)
    const hiddenOwners = owners
      .filter((entry) => !entry.visible)
      .sort(byLongestHidden)

    let acquisitionsLeft = Math.max(
      1,
      Math.floor(options.maxAcquisitionsPerFrame)
    )

    for (let i = 0; i < waiting.length && acquisitionsLeft > 0; i += 1) {
      const entry = waiting[i]
      let size = sizeThatFits(entry)
      while (size !== entry.textureSize && hiddenOwners.length > 0) {
        release(hiddenOwners.shift() as Entry)
        size = sizeThatFits(entry)
      }
      if (size !== undefined) {
        acquire(entry, size)
        entry.allowed = true
        acquisitionsLeft -= 1
      }
    }
  }

  return {
    getResidentBytes: () => residentBytes,
    applyMaterial,
    release,
    assign,
  }
}
